import type { FC, JSX } from "react";

import type { AnyObject, ColumnsType } from "@similarweb/pro-logic-components";
import { HeatmapTable } from "@similarweb/pro-logic-components";
import type { ConfigProviderThemeName } from "@similarweb/pro-logic-components/dist/table/components/ConfigProvider";
import { FlexColumn } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";

import { EmptyStateHeatmap } from "../EmptyStateHeatmap/EmptyStateHeatmap";

import {
    BoxSubtitleLayout,
    GradientOverlay,
    LoaderWrapper,
    StyledBoxContainer,
    StyledHeaderTitle,
    TableContainer,
    TitleBox,
} from "./styledComponent";

interface HeatmapWidgetTableProps {
    title: string;
    tooltip: string;
    data: AnyObject[];
    isLoading: boolean;
    isError?: boolean;
    columns: ColumnsType<AnyObject>;
    formatter?: (value: any) => string;
    subtitleFilters?: Filter[];
    descriptionText?: JSX.Element;
    theme?: ConfigProviderThemeName;
    footerComponent?: React.ReactNode;
    withOpacity?: boolean;
    tableHeight?: string;
}
const HeatmapWidgetTable: FC<HeatmapWidgetTableProps> = ({
    title,
    tooltip,
    data,
    columns,
    isLoading,
    formatter,
    subtitleFilters,
    descriptionText,
    theme = "white",
    footerComponent,
    withOpacity = false,
    tableHeight,
}) => {
    const renderEmptyState = () => <EmptyStateHeatmap />;
    return (
        <StyledBoxContainer>
            <TitleBox>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={translate(tooltip)}>{translate(title)}</BoxTitle>
                    </StyledHeaderTitle>
                    <StyledBoxSubtitle>
                        <BoxSubtitleLayout filters={subtitleFilters} />
                    </StyledBoxSubtitle>
                </FlexColumn>
                <div>{descriptionText}</div>
            </TitleBox>
            {isLoading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                <FlexColumn>
                    <TableContainer tableHeight={tableHeight}>
                        <HeatmapTable
                            formatter={formatter}
                            theme={theme}
                            bordersConfig={{ body: true, groupBorder: true }}
                            columnConfig={columns}
                            data={data}
                            isLoading={isLoading}
                            loaderRowCount={5}
                            sticky={false}
                            noData={{ Component: renderEmptyState }}
                            pagination={{ pageSize: data.length }}
                        />
                        {withOpacity ? <GradientOverlay /> : null}
                    </TableContainer>
                    {footerComponent}
                </FlexColumn>
            )}
        </StyledBoxContainer>
    );
};

export default HeatmapWidgetTable;
